import React, { useEffect, useState } from "react";
import { usePWA } from "@/hooks/usePWA";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, Smartphone, Share, PlusSquare, CheckCircle2, ExternalLink, PackageCheck } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmtSize = (b) => (!b ? "" : b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1e3)} KB`);

export function useAppConfig() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/app/config").then((r) => setCfg(r.data)).catch(() => setCfg({})); }, []);
  return cfg;
}

// Compact header/button trigger
export default function InstallApp({ variant = "button" }) {
  const { canInstall, installed, ios, promptInstall } = usePWA();
  const cfg = useAppConfig();
  const [showIOS, setShowIOS] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const apkOn = cfg?.apk_enabled && cfg?.apk_available;

  if (installed && !apkOn) return null; // running as installed app, nothing to offer

  const handleInstall = async () => {
    if (installed) { window.location.href = "/"; return; }
    if (ios) { setShowIOS(true); return; }
    if (canInstall) {
      const outcome = await promptInstall();
      if (outcome === "accepted") toast.success("Grofkart is being installed!");
      else if (outcome === "unavailable") setShowHelp(true);
      return;
    }
    setShowHelp(true);
  };

  const apkHref = `${BACKEND}/api/app/apk`;

  if (variant === "header") {
    return (
      <>
        {!installed && (
          <button data-testid="header-install-app-btn" onClick={handleInstall}
            className="hidden items-center gap-2 rounded-xl border border-brand/30 bg-brand-soft px-3 py-2 text-sm font-semibold text-brand transition-colors hover:bg-brand hover:text-white md:flex">
            <Download className="h-4 w-4" /> Install App
          </button>
        )}
        <IOSDialog open={showIOS} onClose={() => setShowIOS(false)} />
        <HelpDialog open={showHelp} onClose={() => setShowHelp(false)} />
      </>
    );
  }

  // Full "Get the app" card
  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="get-app-card">
      <div className="flex items-center gap-4 border-b border-slate-100 bg-brand-soft/50 p-5">
        <img src="/icon-192.png" alt="" className="h-14 w-14 rounded-2xl shadow-sm" />
        <div>
          <h2 className="font-head text-lg font-extrabold text-slate-900">Get the Grofkart app</h2>
          <p className="text-sm text-slate-500">Install for a faster, full-screen, app-like experience.</p>
        </div>
      </div>
      <div className="space-y-3 p-5">
        {installed ? (
          <div className="flex items-center gap-2 rounded-xl bg-brand-soft px-4 py-3 text-sm font-semibold text-brand" data-testid="app-installed-msg">
            <CheckCircle2 className="h-5 w-5" /> App installed — you're using it now.
          </div>
        ) : (
          <button data-testid="install-app-btn" onClick={handleInstall}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3.5 text-base font-bold text-white transition-colors hover:bg-brand-deep">
            <Download className="h-5 w-5" /> {ios ? "Add to Home Screen" : "Install App"}
          </button>
        )}

        {apkOn && (
          <a href={apkHref} data-testid="download-apk-btn"
            className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-4 py-3 transition-colors hover:border-brand/40 hover:bg-slate-50">
            <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <PackageCheck className="h-5 w-5 text-brand" /> Download Android APK
            </span>
            <span className="text-xs text-slate-400">{cfg.version && `v${cfg.version}`} {cfg.size ? `· ${fmtSize(cfg.size)}` : ""}</span>
          </a>
        )}
        {apkOn && cfg?.notes && <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">{cfg.notes}</p>}
        <p className="text-center text-[11px] text-slate-400">No app store needed — installs directly from your browser.</p>
      </div>
      <IOSDialog open={showIOS} onClose={() => setShowIOS(false)} />
      <HelpDialog open={showHelp} onClose={() => setShowHelp(false)} />
    </section>
  );
}

function IOSDialog({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="ios-install-dialog" className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-head"><Smartphone className="h-5 w-5 text-brand" /> Install on iPhone</DialogTitle>
          <DialogDescription>Add Grofkart to your Home Screen in two quick steps.</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3">
          <li className="flex items-start gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand text-xs font-bold text-white">1</span>
            <span className="text-sm text-slate-700">Tap the <Share className="mx-1 inline h-4 w-4 text-brand" /> <b>Share</b> button in Safari's toolbar.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand text-xs font-bold text-white">2</span>
            <span className="text-sm text-slate-700">Choose <PlusSquare className="mx-1 inline h-4 w-4 text-brand" /> <b>Add to Home Screen</b>, then tap <b>Add</b>.</span>
          </li>
        </ol>
        <p className="mt-2 rounded-lg bg-brand-soft px-3 py-2 text-xs text-brand">Grofkart will open full-screen, just like a native app.</p>
      </DialogContent>
    </Dialog>
  );
}

function HelpDialog({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="install-help-dialog" className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-head"><ExternalLink className="h-5 w-5 text-brand" /> Install Grofkart</DialogTitle>
          <DialogDescription>Your browser can install this site as an app.</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-slate-700">
          Open your browser menu (⋮) and choose <b>“Install app”</b> or <b>“Add to Home screen”</b>.
          If you don't see it, the app may already be installed, or try Chrome/Edge on Android or desktop.
        </p>
      </DialogContent>
    </Dialog>
  );
}
